"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import Image from "next/image";

interface LayeredFlavorCardProps {
  title: string;
  backSrc: string;
  frontSrc: string;
  className?: string;
}

const LayeredFlavorCard = ({
  title,
  backSrc,
  frontSrc,
  className = "",
}: LayeredFlavorCardProps) => {
  const frontRef = useRef<HTMLDivElement>(null);
  const quickX = useRef<((value: number) => gsap.core.Tween) | null>(null);
  const quickY = useRef<((value: number) => gsap.core.Tween) | null>(null);

  useEffect(() => {
    const node = frontRef.current;
    if (!node) return;

    quickX.current = gsap.quickTo(node, "x", {
      duration: 0.45,
      ease: "power2.out",
    });
    quickY.current = gsap.quickTo(node, "y", {
      duration: 0.45,
      ease: "power2.out",
    });

    return () => {
      gsap.set(node, { x: 0, y: 0 });
    };
  }, []);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const normalizedX = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    const normalizedY = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    const intensity = 30;

    // Move front in opposite direction to cursor while keeping background fixed.
    quickX.current?.(-normalizedX * intensity);
    quickY.current?.(-normalizedY * intensity);
  };

  const handleMouseLeave = () => {
    quickX.current?.(0);
    quickY.current?.(0);
  };

  return (
    <div
      className={`relative h-full w-full overflow-visible ${className}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="absolute inset-[2%] md:inset-[3%] z-10">
        <Image
          src={backSrc}
          alt=""
          aria-hidden="true"
          fill
          loading="eager"
          className="object-contain scale-100 md:scale-[1.03] select-none"
          sizes="(max-width: 1023px) 92vw, 70vw"
          draggable={false}
        />
      </div>

      <div ref={frontRef} className="absolute inset-[2%] md:inset-[3%] z-20 will-change-transform">
        <Image
          src={frontSrc}
          alt={title}
          fill
          loading="eager"
          className="object-contain scale-[0.65] md:scale-[0.85] rotate-0 md:-rotate-4 -translate-y-6 md:-translate-y-16 select-none"
          sizes="(max-width: 1023px) 88vw, 50vw"
          draggable={false}
        />
      </div>
    </div>
  );
};

export default LayeredFlavorCard;
